import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";

import { TemplateLayout } from "../templates/TemplateLayout";
import {
  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG,
  ExperienceVerificationTemplateFormType,
  validationSchema,
} from "./utils";
import { useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { ExperienceVerificationTemplateForm } from "./ExperienceVerificationTemplateForm";

export function ExperienceVerificationTemplateCreationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    enableSettingEducationLevelInExperienceVerificationTemplates,
    releaseInstantEmploymentVerification,
  } = useFlags();

  const goBack = () => navigate("/settings/experience-verification-templates");

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/experience-verification/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Template created successfully!"), { type: "success" });
        goBack();
      },
    }
  );

  const createTemplate = async ({
    name,
    lookback_period_years,
    include_gap_detection,
    gap_threshold_unit,
    minimum_gap_period_months,
    minimum_gap_period_days,
    gap_verification_type,
    reference_rule_options,
    include_cumulative_gap_detection,
    verification_type,
    digital_verification,
  }: ExperienceVerificationTemplateFormType) => {
    const lookback_period_months = lookback_period_years * 12;

    if (!name) {
      showToast(t("Missing Required Fields: Template name"), {
        type: "error",
      });
      return;
    }

    if (!include_gap_detection) {
      await submit({
        name,
        check_arguments: {
          lookback_period_months,
          include_gap_detection: false,
          ...(enableSettingEducationLevelInExperienceVerificationTemplates &&
          verification_type
            ? { verification_type }
            : {}),
          digital_verification,
        },
      });
      return;
    }

    const isDays = gap_threshold_unit === "DAYS";
    const rawGapValue = isDays
      ? minimum_gap_period_days
      : minimum_gap_period_months;
    const gapValue =
      typeof rawGapValue === "string" ? Number(rawGapValue) : rawGapValue;

    if (gapValue == null || isNaN(gapValue)) {
      showToast(t("Missing Required Fields: Gap threshold"), {
        type: "error",
      });
      return;
    }

    await submit({
      name,
      check_arguments: {
        lookback_period_months,
        gap_threshold_unit,
        ...(isDays
          ? { minimum_gap_period_days: gapValue }
          : { minimum_gap_period_months: gapValue }),
        gap_verification_type,
        reference_rule_options,
        include_cumulative_gap_detection,
        ...(enableSettingEducationLevelInExperienceVerificationTemplates &&
        verification_type
          ? { verification_type }
          : {}),
        digital_verification,
      },
    });
  };

  return (
    <Formik<ExperienceVerificationTemplateFormType>
      enableReinitialize
      validateOnMount
      initialValues={{
        name: undefined,
        lookback_period_years:
          EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_LOOKBACK_PERIOD_MONTHS /
          12,
        include_gap_detection: true,
        gap_threshold_unit: "MONTHS",
        minimum_gap_period_months:
          EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_GAP_PERIOD_MONTHS,
        minimum_gap_period_days: undefined,
        gap_verification_type: "STATEMENT",
        reference_rule_options: undefined,
        include_cumulative_gap_detection: false,
        ...(enableSettingEducationLevelInExperienceVerificationTemplates
          ? { verification_type: "EDUCATION_VERIFICATIONS_WITHIN_LOOKBACK" }
          : {}),
        digital_verification: !!releaseInstantEmploymentVerification,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const cast = validationSchema.cast(values, {
          stripUnknown: true,
        }) as ExperienceVerificationTemplateFormType;
        await createTemplate(cast);
      }}
    >
      {({ values, handleSubmit, isSubmitting, isValid, setValues, dirty }) => {
        return (
          <TemplateLayout
            type="create"
            title={t("Create Experience Verification Template")}
            onComplete={handleSubmit}
            submitting={isSubmitting}
            onCancel={goBack}
            disableCompletion={!isValid || !dirty}
          >
            <ExperienceVerificationTemplateForm
              values={values}
              setValues={setValues}
            />
          </TemplateLayout>
        );
      }}
    </Formik>
  );
}
